import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, IUserModel, ModelType, ResourceType, ResourceStatus, IVideoClipResource, VideoClipStatus, IRvcModelAvailable, CreateAudioEditingRequest } from '@packages/global';
import { videoClipResourceApi } from '../../../services/user/videoClipResource.api';
import { videoClipApi } from '../../../services/user/videoclip.api';
import { rvcModelUserApi } from '../../../services/user/rvcModel.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import { ArrowUpIcon, CheckIcon, TrashIcon, PencilSquareIcon, MusicalNoteIcon, AdjustmentsVerticalIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import AudioUpload from '../../common/AudioUpload';
import AudioPlayer from '../../common/AudioPlayer';
import { DropdownSelect, CoinPriceDisplay } from '../../common/form';

interface AudioEditFormProps {
  videoClip: IVideoClip;
  models: IUserModel[];
  modelsLoading: boolean;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  resources?: IVideoClipResource[];
  onTriggerPoll?: () => Promise<void>;
  canvasMode?: boolean;
  externalAddAudio?: { url: string; nonce: number };
  onReferenceAudioCleared?: (url: string) => void;
  onCanvasSubmitSuccess?: () => void;
  initialParams?: Record<string, unknown>;
}

const statusColorConfig: Record<string, string> = {
  [ResourceStatus.PENDING]: 'text-gray-400',
  [ResourceStatus.GENERATING]: 'text-blue-400',
  [ResourceStatus.COMPLETED]: 'text-green-400',
  [ResourceStatus.FAILED]: 'text-red-400'
};

// 竖向滑块弹出组件
function VerticalSlider({
  value,
  onChange,
  min,
  max,
  step,
  label,
  formatValue,
  color = 'blue'
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  label: string;
  formatValue: (v: number) => string;
  color?: 'blue' | 'purple';
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pct = ((value - min) / (max - min)) * 100;
  const trackColor = color === 'blue' ? 'bg-blue-500' : 'bg-purple-500';
  const thumbColor = color === 'blue' ? 'bg-blue-500' : 'bg-purple-500';
  const activeClass = color === 'blue' ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors text-xs border ${isOpen
          ? `${activeClass} border-transparent`
          : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 border-gray-700'
          } cursor-pointer`}
        title={label}
      >
        <AdjustmentsVerticalIcon className="h-4 w-4" />
        <span>{formatValue(value)}</span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 p-3">
          <p className="text-[10px] text-gray-500 text-center mb-1 whitespace-nowrap">{label}</p>
          <div className="flex flex-col items-center">
            <span className="text-[10px] text-gray-500 mb-1">{formatValue(max)}</span>
            <div className="relative h-28 w-6 flex items-center justify-center">
              <div className="absolute h-full w-1.5 bg-gray-700 rounded-full" />
              <div
                className={`absolute bottom-0 w-1.5 ${trackColor} rounded-full transition-all`}
                style={{ height: `${pct}%` }}
              />
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={e => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
                className="absolute h-full w-full opacity-0 cursor-pointer"
                style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
              />
              <div
                className={`absolute w-4 h-4 ${thumbColor} rounded-full border-2 border-white shadow-md pointer-events-none`}
                style={{ bottom: `calc(${pct}% - 8px)` }}
              />
            </div>
            <span className="text-[10px] text-gray-500 mt-1">{formatValue(min)}</span>
          </div>
          <div className="text-center mt-1">
            <span className={`text-sm font-medium ${color === 'blue' ? 'text-blue-400' : 'text-purple-400'}`}>
              {formatValue(value)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AudioEditForm({
  videoClip,
  models,
  modelsLoading,
  refreshVideoClip,
  resources: externalResources = [],
  onTriggerPoll,
  canvasMode = false,
  externalAddAudio,
  onReferenceAudioCleared,
  onCanvasSubmitSuccess,
  initialParams
}: AudioEditFormProps) {
  const [formData, setFormData] = useState({
    srcAudioUrl: '',
    modelId: '',
    rvcModelId: '',
    pitch: 0,
    indexRate: 0.75,
    f0Method: ''
  });
  const [rvcModels, setRvcModels] = useState<IRvcModelAvailable[]>([]);
  const [rvcModelsLoading, setRvcModelsLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { alert, confirm } = useDialog();
  const { success, error } = useToast();
  const { t } = useTranslation(['editor', 'common']);

  const audioEditModels = models.filter(m => m.modelType === ModelType.AUDIO_EDIT);
  const modelOptions = audioEditModels.map(m => ({ value: m.id, label: m.title }));

  const filteredResources = externalResources
    .filter(r => r.resourceType === ResourceType.AUDIO_EDIT)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredResources.length]);

  useEffect(() => {
    if (!modelsLoading && !formData.modelId && audioEditModels.length > 0) {
      setFormData(prev => ({ ...prev, modelId: audioEditModels[0].id }));
    }
  }, [audioEditModels, modelsLoading]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      srcAudioUrl: videoClip.scriptAudioUrl || ''
    }));
  }, [videoClip.id, videoClip.scriptAudioUrl]);

  const lastExternalAudioNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!externalAddAudio) return;
    if (lastExternalAudioNonceRef.current === externalAddAudio.nonce) return;
    lastExternalAudioNonceRef.current = externalAddAudio.nonce;
    if (!externalAddAudio.url) return;

    setFormData(prev => ({
      ...prev,
      srcAudioUrl: externalAddAudio.url,
    }));
  }, [externalAddAudio]);

  const handleSrcAudioChange = (url: string) => {
    if (!url && formData.srcAudioUrl) {
      onReferenceAudioCleared?.(formData.srcAudioUrl);
    }
    setFormData(prev => ({ ...prev, srcAudioUrl: url }));
  };

  useEffect(() => {
    setRvcModelsLoading(true);
    rvcModelUserApi.getAvailable()
      .then(list => {
        setRvcModels(list);
        if (list.length > 0) {
          setFormData(prev => ({ ...prev, rvcModelId: prev.rvcModelId || list[0].applioModelId }));
        }
      })
      .catch(() => { })
      .finally(() => setRvcModelsLoading(false));
  }, []);

  const handleReuseParams = (params: any) => {
    setFormData(prev => ({
      ...prev,
      srcAudioUrl: params.srcAudioUrl || prev.srcAudioUrl,
      modelId: params.modelId || prev.modelId,
      rvcModelId: params.rvcModelId || prev.rvcModelId,
      pitch: params.pitch ?? prev.pitch,
      indexRate: params.indexRate ?? prev.indexRate,
      f0Method: params.f0Method || prev.f0Method
    }));
  };

  const initialParamsAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialParams || initialParamsAppliedRef.current) return;
    initialParamsAppliedRef.current = true;
    handleReuseParams(initialParams);
  }, [initialParams]);

  const handleApplyAudio = async (audioUrl: string) => {
    try {
      await videoClipApi.updateVideoClip(videoClip.id, {
        scriptAudioUrl: audioUrl,
        status: VideoClipStatus.COMPLETED
      });
      success(t('common:audioApplied'));
      if (refreshVideoClip) await refreshVideoClip(videoClip.id);
      if (onTriggerPoll) await onTriggerPoll();
    } catch {
      alert(t('common:applyFailed'));
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    const confirmed = await confirm(t('common:deleteResourceConfirm'), t('common:deleteResource'), 'danger');
    if (!confirmed) return;
    try {
      await videoClipResourceApi.deleteResource(resourceId);
      success(t('common:resourceDeleted'));
      if (onTriggerPoll) await onTriggerPoll();
    } catch {
      error(t('common:deleteFailed'));
    }
  };

  const handleGenerate = async () => {
    if (!formData.modelId) { alert(t('audioEditForm.selectModel')); return; }
    if (!formData.srcAudioUrl) { alert(t('audioEditForm.selectSrcAudio')); return; }
    if (!formData.rvcModelId) { alert(t('audioEditForm.selectRvcModel')); return; }

    try {
      const params: CreateAudioEditingRequest = {
        srcAudioUrl: formData.srcAudioUrl,
        editingType: 'voice_change',
        modelId: formData.modelId,
        rvcModelId: formData.rvcModelId,
        pitch: formData.pitch,
        indexRate: formData.indexRate,
        ...(formData.f0Method ? { f0Method: formData.f0Method } : {})
      };
      await videoClipResourceApi.generateAudioEdit(videoClip.id, params);
      success(t('audioEditForm.voiceChangeSubmitted'));
      onCanvasSubmitSuccess?.();
      if (onTriggerPoll) setTimeout(() => onTriggerPoll!(), 1000);
    } catch {
      error(t('common:submitFailed'));
    }
  };

  const isSubmitDisabled = !formData.modelId || !formData.srcAudioUrl || !formData.rvcModelId;

  const F0_OPTIONS = [
    { value: '', label: t('audioEditForm.f0Default', { defaultValue: 'Default' }) },
    { value: 'pm', label: 'PM' },
    { value: 'harvest', label: 'Harvest' },
    { value: 'crepe', label: 'Crepe' },
    { value: 'rmvpe', label: 'RMVPE' },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* 资源列表 */}
      {!canvasMode && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-6">
            {filteredResources.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <MusicalNoteIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('audioEditForm.noRecords')}</p>
                <p className="text-xs mt-1">{t('audioEditForm.startBelow')}</p>
              </div>
            ) : (
              filteredResources.map((resource) => {
              let params: any = null;
              try {
                params = typeof resource.generationParams === 'string'
                  ? JSON.parse(resource.generationParams)
                  : resource.generationParams;
              } catch { /* ignore */ }
              const statusInfo = statusColorConfig[resource.status];
              const isCompleted = resource.status === ResourceStatus.COMPLETED && resource.resultUrl;

              return (
                <div key={resource.id} className="relative space-y-3 border-2 border-dashed border-gray-800 rounded-lg p-2 group">
                  {params && (
                    <button
                      onClick={() => handleReuseParams(params)}
                      className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white bg-gray-800/80 hover:bg-gray-700 rounded-lg backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 z-10 cursor-pointer"
                      title={t('common:useParams')}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}

                  {params && (
                    <div className="bg-gray-800 rounded-lg p-2 text-xs text-gray-400 space-y-1">
                      {params.rvcModelId && (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">{t('audioEditForm.voiceModel')}:</span>
                          <span className="text-gray-300 truncate">
                            {rvcModels.find(m => m.applioModelId === params.rvcModelId)?.name || params.rvcModelId}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        {params.pitch !== undefined && (
                          <span>{t('audioEditForm.pitchOffset', { defaultValue: 'Pitch' })} <span className="text-gray-300 font-mono">{params.pitch > 0 ? `+${params.pitch}` : params.pitch}</span></span>
                        )}
                        {params.indexRate !== undefined && (
                          <span>{t('audioEditForm.timbreFusion', { defaultValue: 'Fusion' })} <span className="text-gray-300 font-mono">{params.indexRate}</span></span>
                        )}
                      </div>
                    </div>
                  )}

                  {isCompleted && (
                    <AudioPlayer src={resource.resultUrl!} />
                  )}

                  {(resource.status === ResourceStatus.GENERATING || resource.status === ResourceStatus.PENDING) && (
                    <div className="py-8 flex flex-col items-center gap-3 text-gray-400">
                      <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">{t(`generationProgress.${resource.status === ResourceStatus.PENDING ? 'pending' : 'generating'}`)}...</span>
                    </div>
                  )}

                  {resource.status === ResourceStatus.FAILED && (
                    <div className="p-3 bg-red-900/30 rounded-lg text-red-400 text-sm">
                      {resource.errorMessage || t('common:generationFailed')}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    {isCompleted && (
                      <button
                        onClick={() => handleApplyAudio(resource.resultUrl!)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors cursor-pointer"
                      >
                        <CheckIcon className="h-3 w-3" />
                        <span className="text-xs">{t('audioGenForm.applyAudio')}</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteResource(resource.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 rounded-lg transition-colors opacity-0 group-hover:opacity-100 cursor-pointer ml-auto"
                      title={t('common:deleteResource')}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* 底部固定表单 */}
      <div className="flex-shrink-0 border-t border-gray-800 bg-gray-800/30">

        {/* 源音频上传（参考音频生成的弹出式设计） */}
        <div className="px-4 pt-3">
          <AudioUpload
            value={formData.srcAudioUrl}
            onUploadSuccess={handleSrcAudioChange}
            showAudioSelector={true}
            showToneSelector={false}
            showMusicSelector={false}
            placeholder={t('audioEditForm.uploadSrcAudio')}
          />
        </div>

        {/* RVC 声音模型 */}
        <div className="px-4 pt-2">
          {rvcModelsLoading ? (
            <div className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500">{t('common:loading')}</div>
          ) : rvcModels.length === 0 ? (
            <div className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500">{t('audioEditForm.noVoiceModel')}</div>
          ) : (
            <select
              value={formData.rvcModelId}
              onChange={e => setFormData(prev => ({ ...prev, rvcModelId: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-gray-500 cursor-pointer"
            >
              <option value="">{t('audioEditForm.selectVoiceModel')}</option>
              {rvcModels.map(m => (
                <option key={m.id} value={m.applioModelId}>
                  {m.name}{m.description ? ` — ${m.description}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 高级参数折叠区 */}
        <div className="px-4 pt-2">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
          >
            {showAdvanced ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
            <span>{t('audioEditForm.advancedParams')}</span>
          </button>

          {showAdvanced && (
            <div className="mt-2 space-y-2">
              {/* 音调偏移 */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">{t('audioEditForm.pitchOffset')}</span>
                  <span className="text-xs font-mono text-gray-300">
                    {formData.pitch > 0 ? `+${formData.pitch}` : formData.pitch}
                  </span>
                </div>
                <input
                  type="range" min={-12} max={12} step={1}
                  value={formData.pitch}
                  onChange={e => setFormData(prev => ({ ...prev, pitch: parseInt(e.target.value) }))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-[10px] text-gray-600 mt-0.5">
                  <span>-12 {t('audioEditForm.pitchDown')}</span><span>0</span><span>+12 {t('audioEditForm.pitchUp')}</span>
                </div>
              </div>

              {/* 音色融合度 */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">{t('audioEditForm.timbreFusion')}</span>
                  <span className="text-xs font-mono text-gray-300">{formData.indexRate.toFixed(2)}</span>
                </div>
                <input
                  type="range" min={0} max={1} step={0.05}
                  value={formData.indexRate}
                  onChange={e => setFormData(prev => ({ ...prev, indexRate: parseFloat(e.target.value) }))}
                  className="w-full accent-primary"
                />
                <div className="flex justify-between text-[10px] text-gray-600 mt-0.5">
                  <span>{t('audioEditForm.originalVoice')}</span><span>{t('audioEditForm.modelVoice')}</span>
                </div>
              </div>

              {/* 音高提取方法 */}
              <div>
                <label className="text-xs text-gray-400 mb-1 block">{t('audioEditForm.f0Method')}</label>
                <select
                  value={formData.f0Method}
                  onChange={e => setFormData(prev => ({ ...prev, f0Method: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-gray-500 cursor-pointer"
                >
                  {F0_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* 工具栏：模型 + 竖向滑块 + 提交 */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DropdownSelect
                options={modelOptions}
                value={formData.modelId}
                onChange={(value) => setFormData(prev => ({ ...prev, modelId: value }))}
                placeholder={t('lipSyncForm.selectModel')}
                disabled={modelsLoading}
                loading={modelsLoading}
                icon={<MusicalNoteIcon className="h-4 w-4 text-purple-400" />}
              />
              <VerticalSlider
                value={formData.pitch}
                onChange={(v) => setFormData(prev => ({ ...prev, pitch: v }))}
                min={-12}
                max={12}
                step={1}
                label={t('audioEditForm.pitchOffset')}
                formatValue={(v) => v > 0 ? `+${v}` : `${v}`}
                color="blue"
              />
              <VerticalSlider
                value={formData.indexRate}
                onChange={(v) => setFormData(prev => ({ ...prev, indexRate: v }))}
                min={0}
                max={1}
                step={0.05}
                label={t('audioEditForm.timbreFusion')}
                formatValue={(v) => v.toFixed(2)}
                color="purple"
              />
            </div>
            <div className="flex items-center gap-3">
              <CoinPriceDisplay modelId={formData.modelId} />
              <button
                onClick={handleGenerate}
                disabled={isSubmitDisabled}
                className={`p-2 rounded-full transition-colors ${isSubmitDisabled
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-600 hover:bg-gray-500 text-white cursor-pointer'
                  }`}
              >
                <ArrowUpIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
